import { useState } from 'react';

import { format } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { STANDARD_DATE_FORMAT } from '@/utils/date-formatters';

import { InlineCalendar } from './components/inline-calendar';
import { PresetCategoriesList } from './components/preset-category-item';
import { DateRangePickerV2Props, DateRangePreset, DateRangeV2 } from './types';
import { detectPresetFromDates } from './utils';

/**
 * DateRangePickerV2 - A date range picker with preset options
 *
 * Features:
 * - Preset date ranges (Today, Week, Month, Quarter, Year) with submenus
 * - Custom date range selection with inline calendar
 * - Mac-style cascading submenus
 * - Displays "Date Range: {preset}" on trigger button
 *
 * @example
 * ```tsx
 * // With hook (recommended)
 * const { dates, selectedPreset, onChange, onPresetSelect } = useDateRangePickerV2({
 *   defaultValue: DateRangePreset.LastMonth
 * });
 *
 * <DateRangePickerV2
 *   dates={dates}
 *   onChange={onChange}
 *   selectedPreset={selectedPreset}
 *   onPresetSelect={onPresetSelect}
 * />
 *
 * // Controlled usage
 * <DateRangePickerV2
 *   dates={{ startDate: new Date(), endDate: new Date() }}
 *   onChange={handleChange}
 * />
 * ```
 */
export function DateRangePickerV2({
  dates,
  onChange,
  selectedPreset = null,
  onPresetSelect,
  onReset: onResetProp,
  placeholder,
  disabled = false,
  defaultPrefix,
  side = 'bottom',
  align = 'end',
  alignOffset = 0,
  sideOffset = 4,
  collisionPadding = 16,
  avoidCollisions = true,
  useViewportCollisionBoundary = false,
  triggerClassName,
  hiddenPresetCategories,
}: DateRangePickerV2Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  /**
   * Gets the display label for the trigger button
   * Format: "Date Range: {preset label}" or just "Date Range" if no selection
   */
  const getDisplayLabel = (): React.ReactNode => {
    const prefix = defaultPrefix ? defaultPrefix : t('common:dateRangePickerV2.triggerPrefix');

    if (!dates) return placeholder || prefix;

    return (
      <>
        <span className="font-medium text-muted-foreground">{prefix}:</span>{' '}
        <span className="font-medium">
          {`${format(dates.startDate, STANDARD_DATE_FORMAT)} - ${format(dates.endDate, STANDARD_DATE_FORMAT)}`}
        </span>
      </>
    );
  };

  /**
   * Handles preset selection
   */
  const handlePresetSelect = (preset: DateRangePreset) => {
    onPresetSelect?.(preset);
    setOpen(false);
  };

  /**
   * Handles custom date range selection from calendar.
   * If the selected range matches a preset, treats it as a preset selection.
   */
  const handleCustomChange = (newDates: DateRangeV2) => {
    const matchedPreset = detectPresetFromDates(newDates);
    if (matchedPreset) {
      onPresetSelect?.(matchedPreset);
    } else {
      onChange(newDates);
    }
  };

  /**
   * Handles reset action from calendar
   */
  const handleReset = () => {
    if (onResetProp) {
      onResetProp();
    } else {
      onPresetSelect?.(DateRangePreset.Today);
    }
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          color="secondary"
          disabled={disabled}
          className={cn(
            'h-9 justify-between gap-1 border-input px-3 font-normal',
            triggerClassName
          )}
        >
          <span>{getDisplayLabel()}</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side={side}
        align={align}
        alignOffset={alignOffset}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        avoidCollisions={avoidCollisions}
        // Empty array tells Radix to use only the viewport for collision
        // detection — needed when the trigger lives in a clipped overflow
        // container that would otherwise cut the calendar off.
        collisionBoundary={useViewportCollisionBoundary ? [] : undefined}
        className="w-auto min-w-[276px] p-0"
      >
        <div className="p-1">
          {/* Preset categories with submenus */}
          <PresetCategoriesList
            selectedPreset={selectedPreset}
            onSelect={handlePresetSelect}
            hiddenCategories={hiddenPresetCategories}
          />
        </div>

        <DropdownMenuSeparator className="my-0" />

        {/* Inline calendar for custom selection */}
        <InlineCalendar dates={dates} onChange={handleCustomChange} onReset={handleReset} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
